import { forwardRef, MouseEventHandler, useCallback } from 'react';
import styles from './ui-menu-item.module.scss';
import {
    UIAvatar,
    UIAvatarProps,
    UICheckbox,
    UICheckboxProps,
    UIIcon,
    UIIconName,
    UISwitch,
    UISwitchProps,
    UITooltip
} from '..';
import { blue, charcoal } from '../../styles';
import { UISpinner } from '../ui-spinner';

export type UIMenuItemImageProps = {
    src: string;
    alt: string;
    width: number;
};

export type UIMenuItemProps = {
    text: string;
    textColor?: string;
    chevron?: boolean;
    leftImageProps?: UIMenuItemImageProps;
    leftAvatarProps?: UIAvatarProps;
    leftIconName?: UIIconName;
    leftIconColor?: string;
    leftIconOpacity?: number;
    loading?: boolean;
    selected?: boolean;
    switchProps?: UISwitchProps;
    checkboxProps?: UICheckboxProps;
    checkboxPosition?: 'left' | 'right';
    small?: boolean;
    description?: string;
    disabled?: boolean;
    disabledTooltip?: string;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    onMouseOver?: MouseEventHandler<HTMLButtonElement>;
};

export const UIMenuItem = forwardRef<HTMLButtonElement, UIMenuItemProps>(
    (props, ref) => {
        const {
            text,
            textColor = charcoal,
            chevron = false,
            leftImageProps,
            leftAvatarProps,
            leftIconName,
            leftIconColor = textColor,
            leftIconOpacity = 1,
            selected,
            loading,
            switchProps,
            checkboxProps,
            checkboxPosition = 'right',
            description,
            small = false,
            disabled = false,
            disabledTooltip = '',
            onClick,
            onMouseOver
        } = props;

        const content = useCallback(() => {
            const classNames = [styles.container];
            if (small) {
                classNames.push(styles.small);
            }
            if (disabled) {
                classNames.push(styles.disabled);
            }
            return (
                <button
                    ref={ref}
                    onClick={disabled ? undefined : onClick}
                    onMouseOver={disabled ? undefined : onMouseOver}
                    className={classNames.join(' ')}
                    style={{ color: textColor, opacity: disabled ? 0.5 : 1 }}
                    disabled={disabled}
                >
                    <div className={styles.leftItems}>
                        {leftImageProps && (
                            <img
                                src={leftImageProps.src}
                                alt={leftImageProps.alt}
                                style={{
                                    width: leftImageProps.width,
                                    height: 'auto'
                                }}
                            />
                        )}
                        {leftAvatarProps && <UIAvatar {...leftAvatarProps} />}
                        {leftIconName && (
                            <div
                                style={{
                                    opacity: leftIconOpacity,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <UIIcon
                                    name={leftIconName}
                                    size={18}
                                    color={leftIconColor}
                                />
                            </div>
                        )}
                        {checkboxProps && checkboxPosition === 'left' && (
                            <UICheckbox
                                {...checkboxProps}
                                small={small}
                                disabled={disabled}
                            />
                        )}
                        {description ? (
                            <div className={styles.textContent}>
                                <span>{text}</span>
                                <span className={styles.description}>
                                    {description}
                                </span>
                            </div>
                        ) : (
                            text
                        )}
                    </div>
                    <div className={styles.rightItems}>
                        {selected && (
                            <UIIcon name="check" color={blue} size={18} />
                        )}
                        {switchProps && (
                            <UISwitch {...switchProps} disabled={disabled} />
                        )}
                        {checkboxProps && checkboxPosition === 'right' && (
                            <UICheckbox
                                {...checkboxProps}
                                small={small}
                                disabled={disabled}
                            />
                        )}
                        {loading && !disabled && (
                            <UISpinner color={textColor} />
                        )}
                        {chevron && !disabled && (
                            <UIIcon
                                name="chevron-right"
                                color="#000000"
                                size={16}
                            />
                        )}
                    </div>
                </button>
            );
        }, [
            checkboxPosition,
            checkboxProps,
            chevron,
            description,
            disabled,
            leftImageProps,
            leftAvatarProps,
            leftIconColor,
            leftIconName,
            leftIconOpacity,
            loading,
            onClick,
            onMouseOver,
            ref,
            selected,
            small,
            switchProps,
            text,
            textColor
        ]);

        if (disabled && disabledTooltip) {
            return (
                <UITooltip text={disabledTooltip} place="left">
                    {content()}
                </UITooltip>
            );
        }

        return content();
    }
);
